"use client";

import { MAX_SEARCH_DEPTH } from "@/components/library/document-permissions/constants";
import Spinner from "@/components/shared/spinner";
import { useAuth, useOrganization, useUser } from "@clerk/nextjs";
import axios from "axios";
import { usePathname } from "next/navigation";
import { createContext, useContext, useEffect, useState } from "react";
import useSWR, { KeyedMutator } from "swr";
import useSWRImmutable from "swr/immutable";
import { usePostHog } from "posthog-js/react";
import { useSupabaseClient } from "./supabase/supabase-client";

const fetcher = (url: string) => axios.get(url).then((res) => res.data);

const DocIdContext = createContext<{
  docIds: string[];
  existingWorkspaceSettings: Record<string, any>;
  isWorkspaceSettingsLoading: boolean;
  mutateWorkspaceSettings: KeyedMutator<any>;
  latestPath: string | null;
  industry: any[];
  subscriptions: any[];
  subscriptionsLoading: boolean;
  isBasicPlan: boolean;
  isTeamPlan: boolean;
  isBusinessPlan: boolean;
  drivers?: any[];
  suggestedDrivers?: any[];
  dealFilters: any;
  bookmarkedDocumentOrEventIds: string[];
  products: any[];
  competitors: any[];
  segments: any[];
}>({
  docIds: [],
  existingWorkspaceSettings: {},
  isWorkspaceSettingsLoading: true,
  mutateWorkspaceSettings: async () => {},
  latestPath: null,
  industry: [],
  subscriptions: [],
  subscriptionsLoading: true,
  isBasicPlan: false,
  isTeamPlan: false,
  isBusinessPlan: false,
  drivers: [],
  suggestedDrivers: [],
  dealFilters: [],
  bookmarkedDocumentOrEventIds: [],
  products: [],
  competitors: [],
  segments: [],
});

export const DocIdContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { userId, orgId } = useAuth();
  if (!userId || !orgId) {
    return <>{children}</>;
  }
  const posthog = usePostHog();
  // const { data: docIds, isLoading } = useSWR(
  //   !userId
  //     ? null
  //     : `/api/user-permissions/${userId}/get-documents?depth=${MAX_SEARCH_DEPTH}`,
  //   fetcher,
  // );
  const {
    data: existingWorkspaceSettings,
    mutate: mutateWorkspaceSettings,
    isLoading: isWorkspaceSettingsLoading,
  } = useSWR(!orgId ? null : `/api/workspace-settings`, fetcher);

  const { data: clientIndustry, isLoading: isClientIndustryLoading } = useSWR(
    !orgId ? null : `/api/clients/industry`,
    fetcher,
  );
  const { data: dealFilters, isLoading: dealFiltersLoading } = useSWR(
    !orgId ? null : `/api/deals/filters`,
    fetcher,
  );
  const {
    data: existingReasons,
    mutate: reasonsMutate,
    isLoading: reasonsLoading,
  } = useSWR(!orgId ? null : "/api/reasons?includeSuggested=true", fetcher);
  const [latestPath, setLatestPath] = useState<string | null>(null);
  const pathname = usePathname();
  useEffect(() => {
    if (!pathname?.includes("/settings")) {
      setLatestPath(pathname);
    }
  }, [pathname]);
  const { organization } = useOrganization();
  const { data: subscriptions, isLoading: subscriptionsLoading } = useSWR(
    orgId ? `/api/create-subscription?noProductName=true&orgId=${orgId}` : null,
    fetcher,
  );
  const { data: bookmarks } = useSWR(userId ? `/api/bookmarks` : null, fetcher);
  const [bookmarkedDocumentOrEventIds, setBookmarkedDocumentOrEventIds] =
    useState<string[]>([]);
  useEffect(() => {
    setBookmarkedDocumentOrEventIds(
      Array.isArray(bookmarks)
        ? bookmarks?.map((b: any) => b.document_or_event_id) ?? []
        : [],
    );
  }, [bookmarks]);
  const { data: products } = useSWR(!orgId ? null : "/api/products", fetcher);
  const { data: competitors } = useSWR(
    !orgId ? null : "/api/competitors",
    fetcher,
  );
  const { data: segments } = useSWR(!orgId ? null : "/api/segments", fetcher);
  return (
    <DocIdContext.Provider
      value={{
        docIds: [],
        existingWorkspaceSettings: existingWorkspaceSettings || {},
        isWorkspaceSettingsLoading: isWorkspaceSettingsLoading,
        mutateWorkspaceSettings: mutateWorkspaceSettings,
        latestPath: latestPath,
        industry: clientIndustry?.industries?.filter((i: any) => i) || [],
        drivers: existingReasons?.filter((r: any) => !r.suggested) || [],
        suggestedDrivers:
          existingReasons?.filter((r: any) => r.suggested) || [],
        subscriptions: subscriptions || [],
        subscriptionsLoading: subscriptionsLoading,
        isBasicPlan:
          subscriptions?.[0]?.plan?.id ===
          process.env.NEXT_PUBLIC_DEALPAGE_BASICS_PRICE_ID,
        isTeamPlan:
          subscriptions?.[0]?.plan?.id ===
          process.env.NEXT_PUBLIC_DEALPAGE_TEAM_PRICE_ID,
        isBusinessPlan: posthog?.isFeatureEnabled("business-plan") || false,
        dealFilters: dealFilters || {},
        bookmarkedDocumentOrEventIds: bookmarkedDocumentOrEventIds,
        products: products || [],
        competitors: competitors || [],
        segments: segments || [],
      }}
    >
      {children}
    </DocIdContext.Provider>
  );
};

export function useDocIdContext() {
  return useContext(DocIdContext);
}
